import { Box } from '@chakra-ui/react';
import { forwardRef, useCallback, useMemo } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { ContentSearchSkeleton } from '~/components/ContentSearchSkeleton';
import { useFetchCreator } from '~/hooks/api/useFetchCreator';
import { useFetchCreatorContentInfinite } from '~/hooks/api/useFetchCreatorContent';
import { SearchContentEmpty } from '~/screens/SearchContent/components';
import {
  ContentsApiResponseResultsType,
  mapApiSearchContentResultsFromApiToVm,
} from '~/searchApi';

import { SingleContent } from '../../blocks/TalentContent/SingleContent';
import { singleContentStyle } from '../../blocks/TalentContent/styles';
import { useContentSelection } from '../../blocks/TalentContent/useContentSelection';
import { useAddContentStore } from '../useAddContentStore';
import { SelectContent } from './SelectContent';

const gridComponents = {
  List: forwardRef<
    HTMLDivElement,
    {
      style?: React.CSSProperties;
      className?: string;
      children?: React.ReactNode;
    }
  >(({ style, className, children }, ref) => (
    <div
      ref={ref}
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '1rem',
        padding: 0,
        borderRadius: 12,
        ...style,
      }}
    >
      {children}
    </div>
  )),
  Item: Box,
};

export const AddContent = () => {
  const {
    isEditMode,
    creatorId,
    setIsContentDetailModalOpen,
    platforms,
    filters,
    sortBy,
    searchQuery,
  } = useAddContentStore();

  const creator = useFetchCreator(creatorId);
  const creatorContent = useFetchCreatorContentInfinite({
    creatorId: creatorId as string,
    platform: platforms.length > 0 ? platforms : undefined,
    filters: filters,
    orderBy: sortBy,
    searchQuery: searchQuery.length > 0 ? searchQuery : undefined,
    isEnabled: !!creatorId,
  });

  const { selectedContent, baseContents, handleSelectContent, getIsChecked } =
    useContentSelection();

  const handleContentDetailOpen = useCallback(
    (contentId: string) => {
      setIsContentDetailModalOpen(true, contentId);
    },
    [setIsContentDetailModalOpen],
  );

  const { transformedContent, rawContentMap } = useMemo(() => {
    const allContent = creatorContent.data?.pages.flat() || [];
    const transformed = mapApiSearchContentResultsFromApiToVm(allContent);

    // Create a mapping from transformed content ID to raw content
    const rawMap = new Map<string, ContentsApiResponseResultsType>();
    allContent.forEach((rawItem) => {
      rawMap.set(rawItem.id, rawItem);
    });

    return {
      transformedContent: transformed,
      rawContentMap: rawMap,
    };
  }, [creatorContent.data]);

  const handleEndReached = useCallback(() => {
    if (!creatorContent.hasNextPage) return;
    if (creatorContent.isFetching) return;

    creatorContent.fetchNextPage();
  }, [creatorContent]);

  if (creator.isLoading) return <ContentSearchSkeleton />;
  if (creator.error) return <p>Error</p>;
  if (creatorContent.isLoading) return <ContentSearchSkeleton />;
  if (creatorContent.error) return <p>Error</p>;
  if (creatorContent.data?.pages.length === 0)
    return (
      <SearchContentEmpty
        noFilter={
          filters.length === 0 &&
          platforms.length === 0 &&
          sortBy === 'published_at' &&
          searchQuery.length === 0
        }
      />
    );

  // Render original grid layout
  return (
    <>
      <VirtuosoGrid
        style={{
          height: 'calc(100vh - 180px)',
          width: '100%',
        }}
        data={transformedContent}
        overscan={400}
        components={gridComponents}
        endReached={handleEndReached}
        totalCount={transformedContent.length}
        itemContent={(_, contentItem) => {
          const isSelected = getIsChecked(contentItem.id);
          const rawContentItem = rawContentMap.get(contentItem.id);

          if (!rawContentItem) {
            return null;
          }

          return (
            <Box
              key={contentItem.id}
              data-selected={isSelected ? 'true' : 'false'}
              width="100%"
              borderRadius="lg"
              overflow="hidden"
              position="relative"
              sx={singleContentStyle.root}
            >
              <SingleContent
                content={contentItem}
                noRoot
                onClick={handleContentDetailOpen}
              />

              <Box
                // eslint-disable-next-line tailwindcss/no-custom-classname
                className="checkbox--container"
                data-selected={isSelected ? 'true' : 'false'}
                sx={singleContentStyle.checkbox}
              >
                <SelectContent
                  content={rawContentItem}
                  onSelectChange={handleSelectContent}
                  isChecked={isSelected}
                  totalSelectedCount={
                    isEditMode ? baseContents.length : selectedContent.length
                  }
                />
              </Box>
            </Box>
          );
        }}
      />
    </>
  );
};
